import { LOCATION_INITIALIZED } from '@angular/common';
import { Provider } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';

import { POST_AUTH_REDIRECT_KEY } from './auth.keys';
import { AuthService } from './auth.service';

/** Inner budget. Must stay below index.html's BOOT_RENDER_BUDGET_MS by a render allowance. */
export const AUTH_INIT_TIMEOUT_MS = 8000;

/**
 * Never rejects. A rejected LOCATION_INITIALIZED propagates out of the blocking-nav
 * initializer, rejects bootstrapApplication, and renders no shell at all.
 *
 * `defaultValue` is load-bearing, not defensive: init() completes EMPTY on the
 * acquireTokenRedirect arm, and firstValueFrom without it rejects with EmptyError.
 * Never lastValueFrom/toPromise — MSAL-wrapped streams may emit and never complete.
 */
export function authInitPromise(
	auth: AuthService,
	timeoutMs = AUTH_INIT_TIMEOUT_MS
): Promise<void> {
	let timer: ReturnType<typeof setTimeout>;
	const timeout = new Promise<'timeout'>(resolve => {
		timer = setTimeout(() => resolve('timeout'), timeoutMs);
	});
	const settled = firstValueFrom(auth.init().pipe(take(1)), { defaultValue: null })
		.then(() => 'settled' as const)
		.catch(() => 'errored' as const);

	return Promise.race([settled, timeout]).then(outcome => {
		clearTimeout(timer);
		if (outcome !== 'settled') {
			auth.markFailed();
		}
		try {
			applyPostAuthRedirect(auth);
		} catch {
			// Never let a storage/history quirk (e.g. private-browsing restrictions) reject this
			// promise — see the "Never rejects" contract above.
		}
	});
}

/**
 * Rewrites the URL BEFORE the router's initial navigation reads it.
 * Router.initialNavigation() reads location.path(true) at call time and is gated on
 * !hasRequestedNavigation, so replaceState is what it sees — with no competing navigation.
 * Consumed only on an authenticated settle; otherwise cleared, or an abandoned login's URL
 * would replay over whatever the user asked for next.
 */
function applyPostAuthRedirect(auth: AuthService): void {
	const target = localStorage.getItem(POST_AUTH_REDIRECT_KEY);
	if (!target) {
		return;
	}
	localStorage.removeItem(POST_AUTH_REDIRECT_KEY);
	if (auth.status() === 'authenticated') {
		history.replaceState(null, '', target);
	}
}

export function provideAuthInit(): Provider[] {
	return [
		{
			provide: LOCATION_INITIALIZED,
			useFactory: (auth: AuthService) => authInitPromise(auth),
			deps: [AuthService]
		}
	];
}
